import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { entitled } from '@components/routes/routeEntitlement';
import { useHasDataGraphAudiences } from '@components/routes/entitlements';
import { SECTION } from '@hooks/useDocumentTitle/constants';
import { definePageContextRoute, relatedResource, subjectResource } from '@context/pageContext';

/**
 * Every element is lazy — see the note in @components/cohorts/routes on why the
 * route definitions themselves must not reference page modules directly.
 */
const DataGraphAudiencesLayout = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './routesLayout').then((module) => ({
    default: module.DataGraphAudiencesLayout,
  })),
);

const AudienceListPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './audienceListPage').then((module) => ({
    default: module.AudienceListPage,
  })),
);

const AudienceDetailPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './audienceDetailPage').then((module) => ({
    default: module.AudienceDetailPage,
  })),
);

const SyncDetailsPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './syncDetailsPage').then((module) => ({
    default: module.SyncDetailsPage,
  })),
);

const AUDIENCE_PAGE_CONTEXT_ICON = 'audience-group' as const;

/** See the note in @components/botManagement/routes on why these are definitions. */
export const dataGraphAudienceRoutes = (
  <Route
    key={Path.DATA_GRAPH_AUDIENCES}
    path={Path.DATA_GRAPH_AUDIENCES}
    element={entitled(useHasDataGraphAudiences, <DataGraphAudiencesLayout />)}
    handle={{
      section: SECTION.dataGraphAudiences,
      pageContext: definePageContextRoute({
        type: 'audience.list',
        title: 'Audiences',
        iconKey: AUDIENCE_PAGE_CONTEXT_ICON,
      }),
    }}
  >
    <Route index element={<AudienceListPage />} />
    <Route
      path=":id"
      element={<AudienceDetailPage />}
      handle={{
        pageContext: definePageContextRoute({
          type: 'audience.details',
          title: 'Audience details',
          iconKey: AUDIENCE_PAGE_CONTEXT_ICON,
          resources: [subjectResource({ routeParam: 'id', type: 'audience' })],
        }),
      }}
    />
    {/* Its own section rather than an action on the parent: the page is named by
        the audience→destination pair, so `Weekly Actives → Braze - Data graph
        audience syncs` reads better than folding it under the audience. */}
    <Route
      path=":audienceId/syncs/:syncId"
      element={<SyncDetailsPage />}
      handle={{
        section: SECTION.dataGraphAudienceSyncs,
        pageContext: definePageContextRoute({
          type: 'audience.sync',
          title: 'Audience sync',
          iconKey: AUDIENCE_PAGE_CONTEXT_ICON,
          resources: [
            relatedResource({ routeParam: 'audienceId', type: 'audience' }),
            subjectResource({ routeParam: 'syncId', type: 'audienceSync' }),
          ],
        }),
      }}
    />
  </Route>
);
